import { useMemo, useState } from "react";
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  Rule,
  SectionHeader,
  Spinner,
  Stat,
  Tag,
} from "../ui";
import { useBuildReferences, useReferences } from "../hooks/useReferences";
import { useSources } from "../hooks/useSources";
import type { Project, Reference, Source } from "../types";

const FIXED_REFERENCES = [
  {
    citation_number: 1,
    abnt_reference:
      "EUROPEAN MEDICINES AGENCY (EMA). Guideline on setting health-based exposure limits for use in risk identification in the manufacture of different medicinal products in shared facilities (EMA/CHMP/CVMP/SWP/169430/2012). London, 2014.",
  },
  {
    citation_number: 2,
    abnt_reference:
      "INTERNATIONAL COUNCIL FOR HARMONISATION (ICH). Q3C (R8) - Impurities: Guideline for Residual Solvents. 2021.",
  },
  {
    citation_number: 3,
    abnt_reference:
      "ACTIVE PHARMACEUTICAL INGREDIENTS COMMITTEE (APIC). Guideline on Aspects of Cleaning Validation in Active Pharmaceutical Ingredient Plants. Brussels: CEFIC, 2014.",
  },
];

export interface Step3ReferencesProps {
  project: Project;
  onAdvance: (n: number) => void;
}

export function Step3References({ project, onAdvance }: Step3ReferencesProps) {
  const refs = useReferences(project.id);
  const selectedSources = useSources(project.id, { selected: true });
  const build = useBuildReferences(project.id);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyError, setCopyError] = useState(false);

  const sourceReferences = useMemo(
    () =>
      (selectedSources.data ?? [])
        .filter(hasBuiltReference)
        .map(sourceToReference)
        .sort((a, b) => a.citation_number - b.citation_number),
    [selectedSources.data],
  );
  const generated = refs.data?.length ? refs.data : sourceReferences;
  const isLoadingReferences = refs.isPending && selectedSources.isPending;
  const hasGeneratedReferences = generated.length > 0;
  const canGoToExtraction = hasGeneratedReferences || project.current_step >= 4;

  const flash = (id: string) => {
    setCopiedId(id);
    window.setTimeout(() => setCopiedId(null), 1500);
  };

  const copyOne = async (ref: Reference) => {
    try {
      await navigator.clipboard.writeText(ref.abnt_reference);
      setCopyError(false);
      flash(String(ref.citation_number));
    } catch {
      setCopyError(true);
    }
  };

  const copyAll = async () => {
    const text = generated.map((r) => r.abnt_reference).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopyError(false);
      flash("__all__");
    } catch {
      setCopyError(true);
    }
  };

  return (
    <div className="anim-fade">
      <SectionHeader
        number={3}
        eyebrow="Etapa 03 · Bibliografia"
        title="Referências ABNT"
        lede="Norma NBR 6023. As três primeiras entradas são fixas no template Altox (EMA, ICH, APIC). A automação preenche da entrada 04 em diante, na ordem em que as fontes foram aprovadas."
        action={
          <Button
            variant="ink"
            iconRight={Icon.ArrowRight}
            disabled={build.isPending || isLoadingReferences || !canGoToExtraction}
            onClick={() => onAdvance(4)}
          >
            Ir para extração
          </Button>
        }
      />

      <div className="grid grid-cols-3 gap-0 border-y-2 border-[var(--ink)] mb-12">
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat value={FIXED_REFERENCES.length + generated.length} label="Total" size="md" />
        </div>
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat value={FIXED_REFERENCES.length} label="Fixas" sub="template Altox" size="md" />
        </div>
        <div className="px-6 py-5">
          <Stat
            value={generated.length}
            label="Automatizadas"
            sub={`${selectedSources.data?.length ?? 0} selecionada(s)`}
            size="md"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mb-8">
        <Button
          variant="line"
          size="sm"
          icon={copiedId === "__all__" ? Icon.Check : Icon.Copy}
          disabled={!hasGeneratedReferences}
          className={copiedId === "__all__" ? "text-[var(--ok)]" : ""}
          onClick={copyAll}
        >
          {copiedId === "__all__" ? `Copiado (${generated.length})` : "Copiar todas"}
        </Button>
        <Button
          variant="ink"
          size="sm"
          icon={build.isPending ? Icon.Loader : Icon.FileText}
          disabled={build.isPending}
          onClick={() => build.mutate()}
        >
          Reconstruir referências
        </Button>
      </div>

      {copyError && (
        <div className="mb-8">
          <Callout tone="strike" icon={Icon.AlertCircle} title="Não foi possível copiar">
            Selecione e copie manualmente o texto da referência.
          </Callout>
        </div>
      )}

      <Rule className="mb-6">§ 3.1 · Entradas fixas</Rule>
      <ol className="anim-stagger mb-14">
        {FIXED_REFERENCES.map((r) => (
          <FixedRefEntry key={r.citation_number} r={r} />
        ))}
      </ol>

      <Rule className="mb-6">§ 3.2 · Geradas pela automação</Rule>
      {isLoadingReferences ? (
        <div className="py-16 flex items-center justify-center gap-3 caption">
          <Spinner /> Carregando referências...
        </div>
      ) : refs.isError && !hasGeneratedReferences ? (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Referências indisponíveis">
          Não foi possível carregar as referências. Tente construir novamente.
        </Callout>
      ) : !hasGeneratedReferences ? (
        <EmptyState
          icon={Icon.Library}
          title="Nenhuma referência ainda"
          description="Selecione fontes na Etapa 2 e clique em Construir referências."
          action={
            <Button
              variant="ink"
              icon={build.isPending ? Icon.Loader : Icon.FileText}
            disabled={build.isPending}
            onClick={() => build.mutate()}
          >
            Construir referências
            </Button>
          }
        />
      ) : (
        <ol className="anim-stagger">
          {generated.map((r) => (
            <ReferenceRow
              key={`${r.source_id}-${r.citation_number}`}
              r={r}
              copied={copiedId === String(r.citation_number)}
              onCopy={() => copyOne(r)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function hasBuiltReference(src: Source): src is Source & {
  citation_number: number;
  abnt_reference: string;
} {
  return src.selected && src.citation_number != null && !!src.abnt_reference;
}

function sourceToReference(src: Source & {
  citation_number: number;
  abnt_reference: string;
}): Reference {
  return {
    source_id: src.id,
    citation_number: src.citation_number,
    abnt_reference: src.abnt_reference,
    url: src.url,
    document_type: src.document_type ?? "generico",
  };
}

interface FixedReference {
  citation_number: number;
  abnt_reference: string;
}

function FixedRefEntry({ r }: { r: FixedReference }) {
  return (
    <li className="grid grid-cols-[5rem_1fr] gap-6 py-5 border-b border-[var(--rule-2)] items-baseline">
      <div className="text-right">
        <span className="step-num text-[36px] tabular text-[var(--ink)]">
          ({r.citation_number})
        </span>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Tag tone="rule">Template Altox · fixa</Tag>
        </div>
        <p
          className="serif text-[15px] leading-relaxed text-[var(--ink)]"
          style={{ fontFamily: "var(--serif)" }}
        >
          {r.abnt_reference}
        </p>
      </div>
    </li>
  );
}

interface ReferenceRowProps {
  r: Reference;
  copied: boolean;
  onCopy: () => void;
}

function ReferenceRow({ r, copied, onCopy }: ReferenceRowProps) {
  return (
    <li className="grid grid-cols-[5rem_1fr] gap-6 py-5 border-b border-[var(--rule-2)] items-baseline">
      <div className="text-right">
        <span className="step-num text-[36px] tabular text-[var(--ink)]">
          ({r.citation_number})
        </span>
      </div>
      <div>
        <div className="flex items-center justify-between gap-4 mb-2">
          <Tag tone="ink">Automação</Tag>
          <Button
            variant="ghost"
            size="sm"
            icon={copied ? Icon.Check : Icon.Copy}
            aria-label={`Copiar referência ${r.citation_number}`}
            className={copied ? "text-[var(--ok)]" : ""}
            onClick={onCopy}
          >
            {copied ? "Copiado" : "Copiar"}
          </Button>
        </div>
        <p
          className="serif text-[15px] leading-relaxed text-[var(--ink)]"
          style={{ fontFamily: "var(--serif)" }}
        >
          {r.abnt_reference}
        </p>
      </div>
    </li>
  );
}
